import { ActionTree, ActionContext } from 'vuex';

import axios, { HttpStatusCode } from "axios";

import { State } from './state';
import { Mutations } from './mutations';
import { MutationTypes } from './mutation-types';
import { ActionTypes } from './action-types';
import { Errors, Photo, PhotoStatus, Steps } from '@/@types';
import imageCompression from 'browser-image-compression'
import apiClient from '@/http-common';

type AugmentedActionContext = {
  commit<K extends keyof Mutations>(
    key: K,
    payload: Parameters<Mutations[K]>[1],
  ): ReturnType<Mutations[K]>;
} & Omit<ActionContext<State, State>, 'commit'>

export interface Actions {
  [ActionTypes.INIT](
    { commit }: AugmentedActionContext, data: { uuid: string, returnUrl: string },
  ): Promise<void>;
  [ActionTypes.PROCCESS](
    { commit }: AugmentedActionContext, photo: File,
  ): Promise<void>;
  [ActionTypes.ACCEPT](
    { commit }: AugmentedActionContext,
  ): Promise<void>;
}

export const actions: ActionTree<State, State> & Actions = {
  async [ActionTypes.INIT]({ commit }, { uuid, sessionType, returnUrl }: { uuid: string, sessionType: string, returnUrl: string }) {
    commit(MutationTypes.SET_LOADING, true);
    try {
      let photo: Photo;
      if(sessionType === "photo") {
        const response = await apiClient.post(
          `photos/${uuid}`
        );
        photo = response.data as Photo;
      } else {
        photo = {
          uuid: uuid,
          data: "",
          status: PhotoStatus.Empty,
          assessment: {compliant: true, warnings: []} 
        };
      }
      
      commit(MutationTypes.SET_PHOTO, photo);
      commit(MutationTypes.SET_SESSIONTYPE, sessionType);
      commit(MutationTypes.SET_RETURN_URL, returnUrl);
    } catch (error: any) {
      if (axios.isAxiosError(error) && error.response?.status == HttpStatusCode.Locked) {
        commit(MutationTypes.SET_ERROR, Errors.PhotoLocked)
      } else {
        commit(MutationTypes.SET_ERROR, Errors.Unknown)
      }
    } finally {
      commit(MutationTypes.SET_LOADING, false);
    }
  },
  async [ActionTypes.PROCCESS]({ commit, state }, photoFile: File) {
    commit(MutationTypes.SET_LOADING, true);
    try {
      const uuid = state.photo?.uuid;

      const compressingOptions = {
        maxSizeMB: 5,
        maxWidthOrHeight: 3000,
        useWebWorker: true
      }

      const compressedPhotoFile = await imageCompression(photoFile, compressingOptions)

      const formData = new FormData()
      formData.append('file', compressedPhotoFile)
      const response = await apiClient.put(`photos/${uuid}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })

      const photo = response.data as Photo;

      commit(MutationTypes.SET_PHOTO, photo);
      commit(MutationTypes.SET_STEP, Steps.Accept);
    } catch (error) {
      commit(MutationTypes.SET_ERROR, Errors.Unknown)
    } finally {
      commit(MutationTypes.SET_LOADING, false);
    }
  },
  async [ActionTypes.PROCESSDOCUMENT]({ commit, state }, photoFile: File) {
    commit(MutationTypes.SET_LOADING, true);
    try {
      const uuid = state.photo?.uuid;

      const compressingOptions = {
        maxSizeMB: 5,
        maxWidthOrHeight: 3000,
        useWebWorker: true
      }

      const compressedPhotoFile = await imageCompression(photoFile, compressingOptions)

      const formData = new FormData()
      formData.append('file', compressedPhotoFile)
      const response = await apiClient.put(`documents/${uuid}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })

      const document = response.data as Photo;

      commit(MutationTypes.SET_DOCUMENT, document);
      commit(MutationTypes.SET_STEP, Steps.AcceptDocument);
    } catch (error) {
      commit(MutationTypes.SET_ERROR, Errors.Unknown)
    } finally {
      commit(MutationTypes.SET_LOADING, false);
    }
  },
  async [ActionTypes.ACCEPT]({ commit, state }) {
    commit(MutationTypes.SET_LOADING, true);
    try {
      const uuid = state.photo?.uuid;

      await apiClient.put(`photos/${uuid}/accept`)

      commit(MutationTypes.SET_STEP, Steps.Accepted);
    } catch (error) {
      commit(MutationTypes.SET_ERROR, Errors.Unknown)
    } finally {
      commit(MutationTypes.SET_LOADING, false);
    }

    if (state.step == Steps.Accepted) {
      window.location.href = state.returnUrl!
    }
  },
  async [ActionTypes.ACCEPTDOCUMENT]({ commit, state }) {
    window.location.href = state.returnUrl!
  }
};
